import { Box, Flex, Text } from '@chakra-ui/react';
import { ChatAvatar } from '@/components/chat/ChatAvatar';
import { formatChatTime } from '../formatChatTime';
import type { ChatListItem } from '../useCommunicationPage';
import { MutedBell } from './MutedBell';
import { UnreadBadge } from './UnreadBadge';

type Props = {
  item: ChatListItem;
  active: boolean;
  onSelect: () => void;
};

/**
 * The event's group chat, as a raised card above the two sections below it -- the one
 * conversation everybody on the event shares.
 */
export function GeneralChatCard({ item, active, onSelect }: Props) {
  return (
    <Box px={4} pt={3}>
      <Flex
        role="button"
        tabIndex={0}
        align="center"
        gap={3}
        p={3}
        borderRadius="xl"
        borderWidth={1}
        borderColor={active ? 'volyx.action' : 'volyx.borderLight'}
        // Unread tints the card rather than adding a colour of its own.
        bg={item.unreadCount > 0 ? 'volyx.primaryLight' : 'volyx.bg'}
        boxShadow="0 1px 2px rgba(0, 0, 0, 0.04)"
        cursor="pointer"
        _hover={{ borderColor: active ? 'volyx.action' : 'volyx.mediumGray' }}
        _active={{ transform: 'scale(0.995)' }}
        transition="border-color 0.12s ease-out, background 0.12s ease-out, transform 0.12s ease-out"
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
      >
        <ChatAvatar kind="event" stored={item.image} name={item.eventName} size="md" />
        <Box flex={1} minW={0}>
          <Flex justify="space-between" align="baseline" gap={2}>
            <Text
              fontSize="sm"
              fontWeight={item.unreadCount > 0 ? 'bold' : 'semibold'}
              color="gray.800"
              lineClamp={1}
            >
              {item.title}
            </Text>
            <Text
              fontSize="xs"
              // The count badge is the row's only coloured signal now. A blue timestamp
              // beside it read as a second, competing one.
              color="gray.400"
              fontWeight={item.unreadCount > 0 ? 'semibold' : 'normal'}
              flexShrink={0}
            >
              {formatChatTime(item.lastMessageAt ?? item.createdAt)}
            </Text>
          </Flex>
          <Flex align="center" gap={2} mt={1}>
            <Text
              fontSize="sm"
              fontWeight={item.unreadCount > 0 ? 'semibold' : 'normal'}
              color={item.unreadCount > 0 ? 'gray.800' : 'gray.500'}
              lineClamp={1}
              flex={1}
              minW={0}
            >
              {item.preview}
            </Text>
            {item.muted ? <MutedBell /> : null}
          <UnreadBadge count={item.unreadCount} muted={item.muted} />
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}
